import React, { useState } from "react";
import { Table, Button, App, Space, Popconfirm, Tooltip, Modal, Descriptions } from "antd";
import { DeleteOutlined, EditOutlined, PrinterOutlined, InboxOutlined, RedoOutlined } from "@ant-design/icons";
import { useDispatch, useSelector } from "react-redux";
import dayjs from "dayjs";
import { accessChecker, formatToCompanyTimezone } from "../../../lib/functions";
import { useNavigate } from "react-router-dom";
import { deleteAdjustment, fetchAdjustmentRecord, retryAdjustmentDestroy } from "../AdjustmentRecordThunk";
import { useIsAdminUser } from "../../../lib/accessList";
import { useTxLock } from "../../../hooks/useTxLock";
import Swal from "sweetalert2";

const AdjustmentRecordTable = ({ printMode = false }) => {
  const { list, loading, filters } = useSelector(
    (state) => state.adjustmentRecord
  );

  const authInfo = useSelector((state) => state.auth?.auth_info);

  const canEdit = authInfo?.can_edit === 1;
  const canDelete = authInfo?.can_delete === 1;
  const isAdminUser = useIsAdminUser();
  const isTatmeenUAE = authInfo?.is_tatmeen_enabled === "yes" &&
    authInfo?.country === "United Arab Emirates" && (authInfo?.subscription == 'PharmaERP_Pro' || authInfo?.subscription == 'PharmaERP_Standard' || authInfo?.subscription == 'PharmaERP_Basic');

  const hasTatmeenEntryAccess = accessChecker("tatmeen_entry");

  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
  });
  const [tatmeenModal, setTatmeenModal] = useState({
    open: false,
    record: null
  });

  const [retryLoading, setRetryLoading] = useState(false);
  const { isLocked: isTxLocked, lockDateFormatted } = useTxLock();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { modal } = App.useApp();

  const handleTableChange = (pagination) => {
    setPagination(pagination);
  };

  const openTatmeenModal = (record) => {
    setTatmeenModal({
      open: true,
      record
    });
  };

  const handleTatmeenRetry = async (record) => {

    const result = await Swal.fire({

      title: "Retry Destroy?",

      text: `Adjustment ${record.vch_no} will be reprocessed.`,

      icon: "question",

      showCancelButton: true

    });

    if (!result.isConfirmed) return;


    setRetryLoading(true);

    const res = await dispatch(

      retryAdjustmentDestroy(

        record.id

      )

    );

    setRetryLoading(false);


    if (

      res.meta.requestStatus

      ===

      "fulfilled"

    ) {

      Swal.fire(

        "Success",

        "Destroy retry queued.",

        "success"

      );

      setTatmeenModal({

        open: false,

        record: null

      });


      dispatch(

        fetchAdjustmentRecord({

          selectedFilterType:

            filters.filterType,

          selectedRecordType:

            filters.recordType,

          filters

        })

      );

    }
    else {

      Swal.fire(

        "Failed",

        res.payload ||

        "Retry failed",

        "error"

      );

    }

  };
  // -----------------------
  // Columns for WITHOUT ITEM (summary)
  // -----------------------
  const summaryColumns = [
    { title: "No.", render: (_, __, index) => index + 1, width: 60 },
    { title: "Voucher No", dataIndex: "vch_no" },
    {
      title: "Date",
      dataIndex: "date",
      render: (v) =>
        v ? formatToCompanyTimezone(v).split(" ")[0] : "-",
    },
    { title: "Created By", dataIndex: "createdBy" },
    {
      title: "Total",
      dataIndex: "total",
      align: "right",
      render: (val) => Number(val).toFixed(2),
    },
    isTatmeenUAE && hasTatmeenEntryAccess && {

      title: "Tatmeen Status",

      dataIndex:

        "tatmeen_sent_status",

      width: 140,

      align: "center",

      render: (value) => {

        const colors = {

          pending: "#faad14",

          sent: "#52c41a",

          failed: "#ff4d4f",

          cancelled: "#8c8c8c"

        };

        return (

          <span

            style={{

              color:

                colors[value]

                ||

                "#8c8c8c",

              fontWeight: 600

            }}

          >

            {value}

          </span>

        );

      }

    },
    {
      title: "Actions",
      render: (_, record) => {
        const txLocked = isTxLocked(record?.date);

        return (
          <Space>

            {isTatmeenUAE && hasTatmeenEntryAccess && (

              <Tooltip title="Tatmeen Details">

                <Button

                  type="link"

                  icon={<InboxOutlined />}

                  onClick={() =>

                    openTatmeenModal(record)

                  }

                />

              </Tooltip>

            )}

            <Tooltip
              title={
                txLocked
                  ? `This voucher is locked. Transactions up to ${lockDateFormatted} cannot be modified.`
                  : "Edit"
              }
            >
              <Button
                type="link"
                disabled={!(isAdminUser || canEdit) || txLocked}
                icon={<EditOutlined />}
                onClick={() => {
                  if (txLocked) return;

                  navigate(`/inventory/item-adjustment/edit/${record.id}`);
                }}
              />
            </Tooltip>

            <Tooltip
              title={
                txLocked
                  ? `This voucher is locked. Transactions up to ${lockDateFormatted} cannot be modified.`
                  : "Delete"
              }
            >
              <Popconfirm
                title="Are you sure you want to delete this adjustment?"
                disabled={!(isAdminUser || canDelete) || txLocked}
                onConfirm={async () => {
                  if (!(isAdminUser || canDelete) || txLocked) return;

                  try {
                    await dispatch(deleteAdjustment(record.id)).unwrap();

                    modal.success({
                      title: "Adjustment deleted successfully",
                    });
                  } catch (err) {
                    modal.error({
                      title: err.message || "Delete failed",
                    });
                  }
                }}
              >
                <Button
                  type="link"
                  danger
                  disabled={!(isAdminUser || canDelete) || txLocked}
                  icon={<DeleteOutlined />}
                />
              </Popconfirm>
            </Tooltip>
          </Space>
        );
      },
    }

  ].filter(Boolean);

  // -----------------------
  // Columns for WITH ITEM (detailed)
  // -----------------------
  const withItemColumns = [
    { title: "No.", render: (_, __, index) => index + 1, width: 60 },
    { title: "Voucher No", dataIndex: "vch_no" },
    {
      title: "Date",
      dataIndex: "date",
      render: (v) =>
        v ? formatToCompanyTimezone(v).split(" ")[0] : "-",
    },
    { title: "Description", dataIndex: "narration" },
    {
      title: "Total",
      dataIndex: "total",
      align: "right",
      render: (val) => Number(val).toFixed(2),
    },
    {
      title: "Actions",
      render: (_, record) => (
        <Space>
          <Button
            type="link"
            disabled={!(isAdminUser || canEdit)}
            icon={<EditOutlined />}
            onClick={() =>
              navigate(`/inventory/item-adjustment/edit/${record.id}`)
            }
          />

          <Popconfirm
            title="Are you sure you want to delete this adjustment?"
            onConfirm={async () => {
              if (!(isAdminUser || canDelete)) return;
              try {
                await dispatch(deleteAdjustment(record.id)).unwrap();
                modal.success({ title: "Adjustment deleted successfully" });
              } catch (err) {
                modal.error({ title: err.message || "Delete failed" });
              }
            }}
          >
            <Button type="link" danger disabled={!(isAdminUser || canDelete)} icon={<DeleteOutlined />} />
          </Popconfirm>
        </Space>
      ),
    }

  ].filter(Boolean);

  const detailColumns = [
    { title: "Sl No.", render: (_, __, index) => index + 1, width: 60 },
    { title: "Item", dataIndex: "item_name" },
    { title: "Rate", dataIndex: "item_rate", align: "right" },
    { title: "Qty", dataIndex: "adjust_qty", align: "right" },
    {
      title: "Total",
      dataIndex: "item_total",
      align: "right",
      render: (val) => Number(val || 0).toFixed(2),
    },
  ];

  const AdjDetailColumns = [
    { title: "Sl No.", render: (_, __, index) => index + 1, width: 60 },
    { title: "Voucher No", dataIndex: "vch_no" },
    {
      title: "Date",
      dataIndex: "date",
      render: (v) =>
        v ? formatToCompanyTimezone(v).split(" ")[0] : "-",
    },
    { title: "Item Name", dataIndex: "item_name", align: "right" },
    { title: "Item Rate", dataIndex: "item_rate", align: "right" },
    { title: "Qty", dataIndex: "adjust_qty", align: "right" },
    {
      title: "Total",
      dataIndex: "total",
      align: "right",
      render: (val) => Number(val || 0).toFixed(2),
    },
  ];

  // -----------------------
  // Grand Totals
  // -----------------------
  let totals;
  if (filters.recordType === "with item") {
    totals = list.reduce(
      (acc, row) => {
        acc.total += Number(row.total || 0);
        acc.master_qty += Number(row.master_qty || 0);
        acc.base_qty += Number(row.base_qty || 0);
        return acc;
      },
      { total: 0, master_qty: 0, base_qty: 0 }
    );
  } else {
    totals = list.reduce(
      (acc, row) => {
        acc.total += Number(row.total || 0);
        return acc;
      },
      { total: 0 }
    );
  }

  const expandable =
    filters.recordType === "with item" &&
      (filters.filterType === "All" || filters.filterType === "By User")
      ? {
        expandedRowRender: (record) => {
          const subTotals = record.details.reduce(
            (acc, item) => {
              acc.masterQty += Number(item.item_qty) || 0;
              acc.baseQty += Number(item.retail_qty) || 0;
              acc.total += Number(item.item_total) || 0;
              return acc;
            },
            { masterQty: 0, baseQty: 0, total: 0 }
          );
          return (
            <Table
              rowKey={(row, idx) => `${record.id}-detail-${idx}`}
              columns={detailColumns}
              dataSource={record.details}
              pagination={false}
              size="small"
              summary={() => (
                <Table.Summary.Row>
                  <Table.Summary.Cell index={0} colSpan={4}>
                    <strong>
                      Sub Total : Master U. Qty = {subTotals.masterQty}, Base
                      U. Qty = {subTotals.baseQty}
                    </strong>
                  </Table.Summary.Cell>
                  <Table.Summary.Cell index={5} align="right">
                    <strong>{subTotals.total.toFixed(2)}</strong>
                  </Table.Summary.Cell>
                </Table.Summary.Row>
              )}
            />
          );
        },
        rowExpandable: (record) => record.details?.length > 0,
      }
      : undefined;

  return (
    <>
      <Table
        rowKey="id"
        columns={
          filters.filterType !== "All" && filters.filterType !== "By User"
            ? AdjDetailColumns
            : filters.recordType === "with item"
              ? withItemColumns
              : summaryColumns
        }
        dataSource={list}
        loading={loading}
        pagination={{
          ...pagination,
          showSizeChanger: true,
          pageSizeOptions: ["10", "20", "50"],
        }}
        onChange={handleTableChange}
        bordered
        expandable={expandable}
        scroll={printMode ? undefined : { x: "max-content" }}
        size={printMode ? "small" : "middle"}
        summary={() => {
          if (
            filters.recordType === "with item" &&
            (filters.filterType === "All" || filters.filterType === "By User")
          ) {
            const grand = list.reduce(
              (acc, row) => {
                const subMaster = row.details?.reduce(
                  (sum, d) => sum + Number(d.item_qty || 0),
                  0
                );
                const subBase = row.details?.reduce(
                  (sum, d) => sum + Number(d.retail_qty || 0),
                  0
                );
                acc.masterQty += subMaster || 0;
                acc.baseQty += subBase || 0;
                acc.total += Number(row.total || 0);
                return acc;
              },
              { masterQty: 0, baseQty: 0, total: 0 }
            );

            return (
              <Table.Summary.Row>
                <Table.Summary.Cell index={0} colSpan={5}>
                  <strong>
                    Grand Total : Master U. Qty = {grand.masterQty}, Base U. Qty ={" "}
                    {grand.baseQty}
                  </strong>
                </Table.Summary.Cell>
                <Table.Summary.Cell index={7} align="right">
                  <strong>{totals.total.toFixed(2)}</strong>
                </Table.Summary.Cell>
                {!printMode && (
                  <Table.Summary.Cell index={8}></Table.Summary.Cell>
                )}
              </Table.Summary.Row>
            );
          } else if (
            filters.filterType !== "All" &&
            filters.filterType !== "By User"
          ) {
            const grand = list.reduce(
              (acc, row) => {
                acc.masterQty += row.adjust_qty || 0;
                acc.baseQty += row.retail_qty || 0;
                acc.total += Number(row.total || 0);
                return acc;
              },
              { masterQty: 0, baseQty: 0, total: 0 }
            );

            return (
              <Table.Summary.Row>
                <Table.Summary.Cell index={0} colSpan={6}>
                  <strong>
                    Grand Total : Master U. Qty = {grand.masterQty}, Base U. Qty ={" "}
                    {grand.baseQty}
                  </strong>
                </Table.Summary.Cell>
                <Table.Summary.Cell index={7} align="right">
                  <strong>{totals.total.toFixed(2)}</strong>
                </Table.Summary.Cell>
                {/* {!printMode && (
                <Table.Summary.Cell index={7}></Table.Summary.Cell>
              )} */}
              </Table.Summary.Row>
            );
          } else {
            return (
              <Table.Summary.Row>
                <Table.Summary.Cell index={0} colSpan={4}>
                  <strong>Grand Total</strong>
                </Table.Summary.Cell>
                <Table.Summary.Cell index={5} align="right">
                  {totals.total.toFixed(2)}
                </Table.Summary.Cell>
                {/* {!printMode && <Table.Summary.Cell index={6}></Table.Summary.Cell>} */}
              </Table.Summary.Row>
            );
          }
        }}
      />



      <Modal

        open={tatmeenModal.open}

        width={700}

        footer={
          hasTatmeenEntryAccess &&
            (
              tatmeenModal.record?.tatmeen_sent_status === "failed" ||
              tatmeenModal.record?.tatmeen_sent_status === "not_started"
            )
            ? [
              <Button
                key="retry"
                type="primary"
                loading={retryLoading}
                icon={<RedoOutlined />}
                onClick={() =>
                  handleTatmeenRetry(tatmeenModal.record)
                }
              >
                Retry Destroy
              </Button>,
            ]
            : null
        }

        onCancel={() =>
          setTatmeenModal({
            open: false,
            record: null
          })
        }
      >
        {tatmeenModal.record && (
          <Descriptions
            bordered
            column={1}
            title="Tatmeen Details"
          >
            <Descriptions.Item label="SSCC Code">
              {
                tatmeenModal.record
                  .sscc_code
                ||
                "-"
              }
            </Descriptions.Item>
            <Descriptions.Item label="Status">
              {
                tatmeenModal.record
                  .tatmeen_sent_status
                ||
                "-"
              }

            </Descriptions.Item>
            <Descriptions.Item label="SSCC Status">

              {

                tatmeenModal.record

                  .tatmeen_sscc_status

                ||

                "-"

              }

            </Descriptions.Item>

            <Descriptions.Item label="Message ID">

              {

                tatmeenModal.record

                  .tatmeen_message_id

                ||

                "-"

              }

            </Descriptions.Item>

            <Descriptions.Item label="Retry Count">

              {

                tatmeenModal.record

                  .tatmeen_retry_count

                ||

                0

              }

            </Descriptions.Item>

            <Descriptions.Item label="Failure Reason">

              {

                tatmeenModal.record

                  .tatmeen_failure_reason

                ||

                "-"

              }

            </Descriptions.Item>

            <Descriptions.Item label="Manual Action">

              {

                tatmeenModal.record

                  .tatmeen_manual_action_required

                ||

                "-"

              }

            </Descriptions.Item>

            <Descriptions.Item label="Last Retry">

              {

                tatmeenModal.record

                  .tatmeen_last_retry_at

                ||

                "-"

              }

            </Descriptions.Item>

          </Descriptions>

        )}

      </Modal>
    </>

  );
};

export default AdjustmentRecordTable;
